/**
 * PillarChart — one pillar's metrics as lines on ONE axis (Recharts, pinned 2.15.4).
 *
 * One axis is the whole discipline here. Two y-scales would let the chart invent a
 * correlation out of an arbitrary alignment, so a mixed-unit pillar is either faceted to a
 * single unit or indexed to target — never plotted against two scales. In pct_of_target a
 * dashed rule at 100 marks "on plan", which is what makes the normalised view readable.
 *
 * Lines carry identity by colour and nothing else: no per-point dots (60 of them on a
 * five-series year is noise, and unlike the single-metric TrendChart they would be
 * decoration — RAG is not the subject here), an activeDot on hover for the hit target, and
 * connectNulls off so a series that stops in July STOPS in July rather than being bridged
 * to December. Text wears text tokens; only the marks wear series colour.
 *
 * The one thing a line says besides identity is whether it was MEASURED. A forecast run is
 * dashed, in the series' own colour, joined to the solid run at the last complete month —
 * see forecastSplit for the keys. Colour is identity; the dash is provenance.
 */

import React from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { MetricFormat } from '../../types/metrics'
import type { PlotAlign } from './plotAlign'
import { AXIS_FALLBACK } from './plotAlign'
import { useTokens } from './charts/useTokens'
import { TREND_HEX } from './trendColors'
import PillarTooltip from './PillarTooltip'
import { PILLAR_SERIES_HEX, type PillarPlotModel } from './pillarSeries'
import { forecastKey, measuredKey } from './forecastSplit'
import { useTheme } from '../../contexts/ThemeContext'

function axisLabel(value: number, mode: PillarPlotModel['mode'], unit: MetricFormat | null): string {
  if (mode === 'pct_of_target') return `${value}%`
  if (unit === 'gbp_k') return `£${value}k`
  if (unit === 'pct') return `${value}%`
  return `${value}`
}

/** One series, as TWO lines over the same rows: measured months solid, forecast months
 *  dashed (forecastSplit owns the keys and the join point). Recharts strokes a whole Line
 *  one way, so this is what "solid to August, dashed to December" costs — and it is worth
 *  it, because £1,331k of September forecast drawn like £479k of invoiced July revenue
 *  would present one as the other.
 *
 *  dot={false} because 60 dots on a five-series year is noise and, unlike the single-metric
 *  TrendChart, they would carry nothing; the activeDot is the hover target.
 *  connectNulls={false} so a line that stops in July stops in July. */
function seriesLines(
  series: PillarPlotModel['series'][number],
  palette: readonly string[]
): React.ReactElement[] {
  const stroke = palette[series.colorIndex % palette.length]
  const shared = {
    type: 'monotone' as const,
    stroke,
    strokeWidth: 2,
    dot: false,
    activeDot: { r: 5 },
    connectNulls: false,
    isAnimationActive: false,
  }
  return [
    <Line key={`${series.metricId}-m`} name={series.title} dataKey={measuredKey(series.metricId)} {...shared} />,
    // legendType/name suppressed: the panel's own Legend already names the metric once, and
    // a second entry would read as a second metric rather than the same one, later.
    <Line
      key={`${series.metricId}-f`}
      dataKey={forecastKey(series.metricId)}
      strokeDasharray="5 4"
      {...shared}
    />,
  ]
}

/** The y axis fills the Metric column, and half a period column pads each end of the x
 *  axis: the plot area then covers the period columns and every point sits on its own
 *  column's centre. Unmeasured (no matrix, or the first paint) falls back to a plain axis. */
function axisGeometry(align?: PlotAlign | null): { axisWidth: number; pad: number } {
  if (!align) return { axisWidth: AXIS_FALLBACK, pad: 0 }
  return { axisWidth: align.labelWidth, pad: align.columnWidth / 2 }
}

/** In % of target, 100 is the line that makes the normalised view readable. */
function onPlanRule(model: PillarPlotModel, colour: string): React.ReactElement | null {
  if (model.mode !== 'pct_of_target') return null
  return (
    <ReferenceLine
      y={100}
      stroke={colour}
      strokeDasharray="4 4"
      label={{ value: 'on plan', position: 'right', fontSize: 9, fill: colour }}
    />
  )
}

export default function PillarChart({
  model,
  align,
}: {
  model: PillarPlotModel
  align?: PlotAlign | null
}): React.JSX.Element {
  const { theme } = useTheme()
  const tokens = useTokens()
  const palette = PILLAR_SERIES_HEX[theme === 'light' ? 'light' : 'dark']
  const plotted = model.series.filter((s) => s.plotted)
  const { axisWidth, pad } = axisGeometry(align)
  return (
    <ResponsiveContainer width="100%" height={230}>
      <LineChart data={model.rows} margin={{ top: 10, right: 0, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={TREND_HEX.grid} vertical={false} />
        {onPlanRule(model, tokens.mut2)}
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: tokens.mut2 }}
          interval="preserveStartEnd"
          minTickGap={14}
          padding={{ left: pad, right: pad }}
        />
        <YAxis
          tickFormatter={(value: number) => axisLabel(value, model.mode, model.unit)}
          tick={{ fontSize: 10, fill: tokens.mut2 }}
          width={axisWidth}
        />
        <Tooltip content={<PillarTooltip model={model} palette={palette} />} cursor={{ stroke: TREND_HEX.grid, strokeWidth: 1 }} />
        {plotted.flatMap((series) => seriesLines(series, palette))}
      </LineChart>
    </ResponsiveContainer>
  )
}
